import type { SvgIconComponent } from "@mui/icons-material";
import type { SelectChangeEvent } from "@mui/material";
import { Checkbox, InputAdornment, ListItemText, ListSubheader, MenuItem, Select } from "@mui/material";
import { useMemo, useState } from "react";
import { stringComparerBy } from "../utilities/arrayHelper";
import SearchField from "./inputs/SearchField";
import TypographyTooltipEllipsis from "./TypographyTooltipEllipsis";

interface Props<T> {
  placeholder: string;
  options: T[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  getOptionValue: (option: T) => string;
  getOptionLabel: (option: T) => string;
  getOptionDescription?: (option: T) => string;
  StartIcon?: SvgIconComponent;
  sortOptions?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  width?: string | number;
  searchable?: boolean;
}

const MultiSelectFilter = <T,>({
  placeholder,
  options,
  selectedValues,
  onChange,
  getOptionValue,
  getOptionLabel,
  getOptionDescription,
  StartIcon,
  sortOptions,
  disabled,
  fullWidth,
  width,
  searchable,
}: Props<T>) => {
  const [searchTerm, setSearchTerm] = useState("");

  const resolvedOptions = useMemo(
    () =>
      (sortOptions ? [...options].sort(stringComparerBy(getOptionLabel)) : options).map((o) => ({
        value: getOptionValue(o),
        label: getOptionLabel(o),
        description: getOptionDescription?.(o),
      })),
    [getOptionDescription, getOptionLabel, getOptionValue, options, sortOptions]
  );

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredOptions = normalizedSearchTerm
    ? resolvedOptions.filter(({ label }) => label.toLowerCase().includes(normalizedSearchTerm))
    : resolvedOptions;

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Keep keystrokes from the menu's typeahead navigation; Escape must still close the menu.
    if (e.key !== "Escape") {
      e.stopPropagation();
    }
  };

  const handleChange = (e: SelectChangeEvent<string[]>) => {
    const { value } = e.target;
    const valueArray = typeof value === "string" ? value.split(",") : value;
    onChange(valueArray);
  };

  return (
    <Select
      disabled={disabled}
      displayEmpty
      multiple
      fullWidth={fullWidth}
      value={selectedValues}
      startAdornment={
        StartIcon && (
          <InputAdornment position="start">
            <StartIcon color={disabled ? "disabled" : "action"} />
          </InputAdornment>
        )
      }
      sx={(t) => ({
        width: fullWidth ? undefined : (width ?? "14rem"),
        minWidth: 0,
        ".MuiInputBase-input": {
          color: selectedValues.length === 0 ? t.palette.text.secondary : t.palette.text.primary,
        },
      })}
      renderValue={(selected) =>
        selected.length === 0
          ? placeholder
          : resolvedOptions
              .filter(({ value }) => selected.includes(value))
              .map(({ label }) => label)
              .join(", ")
      }
      onOpen={() => setSearchTerm("")}
      onChange={handleChange}
      MenuProps={{ sx: { maxHeight: "36rem" }, autoFocus: !searchable }}
    >
      {searchable && (
        <ListSubheader sx={{ py: 1 }}>
          <SearchField fullWidth autoFocus onSearch={setSearchTerm} onKeyDown={handleSearchKeyDown} />
        </ListSubheader>
      )}
      {filteredOptions.map(({ value, label, description }) => (
        <MenuItem key={value} value={value} dense>
          <Checkbox checked={selectedValues.includes(value)} />
          <ListItemText
            sx={{ maxWidth: "24rem" }}
            primary={
              <TypographyTooltipEllipsis
                text={label}
                typographyProps={{ component: "span", sx: { display: "block" } }}
              />
            }
            secondary={
              description ? (
                <TypographyTooltipEllipsis
                  text={description}
                  typographyProps={{ component: "span", variant: "caption", sx: { display: "block" } }}
                />
              ) : undefined
            }
          />
        </MenuItem>
      ))}
    </Select>
  );
};

export default MultiSelectFilter;
